import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import {
  CostAnalysisParams,
  CostAnalysisView,
  CostDimension,
  NONE_VALUE,
} from "../../store/apis/costAnalysisApi";
import {
  DIMENSION_LABEL,
  EXTRA_DIMENSIONS,
  MAIN_DIMENSIONS,
  valueLabel,
} from "./costAnalysisFormat";

const MONTH_RE = /^\d{4}-\d{2}$/;

interface Props {
  value: CostAnalysisParams;
  onChange: (next: CostAnalysisParams) => void;
  /** La última respuesta: de ella salen los valores de cada filtro. */
  view: CostAnalysisView | undefined;
}

/** Periodo, agrupación y filtros del análisis de costes. */
const CostAnalysisControls: React.FC<Props> = ({ value, onChange, view }) => {
  const [showMore, setShowMore] = useState(false);
  const [from, setFrom] = useState(value.from);
  const [to, setTo] = useState(value.to);
  const [margin, setMargin] = useState(
    value.targetMargin === null ? "" : String(value.targetMargin)
  );

  // Los campos de texto se aplican cuando son válidos y se deja de escribir.
  useEffect(() => {
    const id = setTimeout(() => {
      if (!MONTH_RE.test(from) || !MONTH_RE.test(to) || from > to) return;
      if (from !== value.from || to !== value.to)
        onChange({ ...value, from, to });
    }, 500);
    return () => clearTimeout(id);
  }, [from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = setTimeout(() => {
      const parsed =
        margin.trim() === "" ? null : Number(margin.replace(",", "."));
      if (parsed !== null && !(parsed >= 0 && parsed < 100)) return;
      if (parsed !== value.targetMargin)
        onChange({ ...value, targetMargin: parsed });
    }, 500);
    return () => clearTimeout(id);
  }, [margin]); // eslint-disable-line react-hooks/exhaustive-deps

  const rangeInvalid = MONTH_RE.test(from) && MONTH_RE.test(to) && from > to;

  const toggleGroup = (d: CostDimension) => {
    const groupBy = value.groupBy.includes(d)
      ? value.groupBy.filter((x) => x !== d)
      : [...value.groupBy, d];
    onChange({ ...value, groupBy });
  };

  const companyNames = new Map(
    (view?.companies ?? []).map((c) => [c.id, c.name])
  );

  const optionsFor = (d: CostDimension): string[] => {
    if (d === "company") return (view?.companies ?? []).map((c) => c.id);
    return (view?.options?.[d] ?? []).map((v) => v ?? NONE_VALUE);
  };

  const filter = (d: CostDimension) => {
    const selected = value.filters[d] ?? [];
    // Lo seleccionado sigue en la lista aunque ya no salga en el periodo.
    const options = Array.from(new Set([...optionsFor(d), ...selected]));
    return (
      <Autocomplete
        key={d}
        multiple
        size="small"
        limitTags={2}
        options={options}
        value={selected}
        onChange={(_, next) =>
          onChange({ ...value, filters: { ...value.filters, [d]: next } })
        }
        getOptionLabel={(v) =>
          valueLabel(d, v === NONE_VALUE ? null : v, companyNames)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={DIMENSION_LABEL[d]}
            placeholder={selected.length ? "" : "Todos"}
          />
        )}
        sx={{ minWidth: 200, flex: "1 1 200px" }}
      />
    );
  };

  const groupChip = (d: CostDimension) => {
    const position = value.groupBy.indexOf(d);
    const active = position >= 0;
    return (
      <Chip
        key={d}
        label={
          active ? `${position + 1}. ${DIMENSION_LABEL[d]}` : DIMENSION_LABEL[d]
        }
        color={active ? "primary" : "default"}
        variant={active ? "filled" : "outlined"}
        onClick={() => toggleGroup(d)}
        aria-pressed={active}
      />
    );
  };

  return (
    <Stack gap={2}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        gap={2}
        alignItems={{ md: "center" }}
      >
        <TextField
          size="small"
          type="month"
          label="Desde"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={rangeInvalid}
        />
        <TextField
          size="small"
          type="month"
          label="Hasta"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText={
            rangeInvalid ? "No puede ser anterior a Desde" : undefined
          }
          error={rangeInvalid}
        />
        <TextField
          size="small"
          label="Margen objetivo"
          value={margin}
          onChange={(e) => setMargin(e.target.value)}
          placeholder="p. ej. 40"
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          inputProps={{ inputMode: "decimal" }}
          helperText="Calcula el precio de venta que lo da"
          sx={{ width: 220 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={value.includeUnmargined}
              onChange={(e) =>
                onChange({ ...value, includeUnmargined: e.target.checked })
              }
            />
          }
          label="Incluir paid media que paga el cliente (sin margen)"
        />
      </Stack>

      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={1}
        >
          Agrupar por (en el orden en que las eliges)
        </Typography>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {MAIN_DIMENSIONS.map(groupChip)}
          {showMore && EXTRA_DIMENSIONS.map(groupChip)}
        </Stack>
      </Box>

      <Stack direction="row" gap={2} flexWrap="wrap">
        {/* Mes también: al abrir un grupo por mes, el filtro tiene que verse para poder quitarlo */}
        {MAIN_DIMENSIONS.map(filter)}
      </Stack>

      <Collapse in={showMore} unmountOnExit>
        <Stack direction="row" gap={2} flexWrap="wrap">
          {EXTRA_DIMENSIONS.map(filter)}
        </Stack>
      </Collapse>

      <Box>
        <Button
          size="small"
          onClick={() => setShowMore((s) => !s)}
          endIcon={showMore ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {showMore ? "Menos dimensiones" : "Más dimensiones"}
        </Button>
      </Box>
    </Stack>
  );
};

export default CostAnalysisControls;
